import React, { useCallback } from 'react';
import { useT } from '../i18n';
import { SHOP_CATEGORIES, SHOP_LIMITS, SHOP_SOCIALS } from '../constants/shop';
import LocationPicker from './LocationPicker';
import AvatarPicker from './AvatarPicker';
import './DesignerProfileFields.css';
import './ShopProfileFields.css';

// ── SHOP PROFILE FIELDS ──────────────────────────────────────────────────────
// The single editor for a shop's profile, rendered BOTH in registration and in
// Settings — the same rule as DesignerProfileFields, and for the same reason: a
// duplicated form is how "valid at signup, rejected on save" bugs happen.
//
// IT REUSES THE `dpf-*` CLASSES ON PURPOSE. This is the designer form's twin,
// laid out identically and living on the same screens, so it should look
// identical — and a copied 250-line stylesheet is two places to fix every
// future spacing change. Only what is genuinely new here (the delivery switch,
// the social rows) gets `spf-*` classes of its own.
//
// Fully controlled: the parent owns `value` and validation. This component
// renders and reports changes, so registration and Settings cannot disagree
// about what is acceptable.
// `roomy` — see DesignerProfileFields. A shop's fields are all the wide kind,
// so `.dpf.spf` overrides back to a single column either way; the flag is
// carried so both forms take the same props and neither is a special case.
export default function ShopProfileFields({ value, onChange, errors = {}, disabled = false, roomy = false }) {
  const { t } = useT();

  // Every edit goes through here, so the parent always receives a complete,
  // self-consistent profile object.
  const set = useCallback((patch) => onChange({ ...value, ...patch }), [value, onChange]);

  const categories = value.categories || [];

  function toggleCategory(key) {
    const on = categories.includes(key);
    if (!on && categories.length >= SHOP_LIMITS.categories.max) return;   // cap silently
    set({ categories: on ? categories.filter(c => c !== key) : [...categories, key] });
  }

  const setSocial = (id, v) => set({ socials: { ...(value.socials || {}), [id]: v } });

  const err = (k) => (errors[k] ? <span className="dpf-err">{t(errors[k])}</span> : null);

  return (
    // `spf` alongside `dpf` is what lets the shared stylesheet be overridden
    // where the two forms genuinely differ — see ShopProfileFields.css.
    <div className={`dpf spf${roomy ? ' dpf--roomy' : ''}`}>
      {/* The logo IS the avatar — one picture per account, stored in the same
          place, so the cropper, the profile card and every byline that already
          knows how to show a round image need no shop-specific path. */}
      <AvatarPicker
        value={value.avatarUrl}
        onChange={url => set({ avatarUrl: url })}
        disabled={disabled}
        label={t('shop_logo')}
      />

      {/* ── name (required) ── */}
      <label className="dpf-field dpf-field--name">
        <span className="dpf-label">{t('shop_name')} <b className="dpf-req">*</b></span>
        <input
          className={`dpf-input${errors.name ? ' dpf-input--err' : ''}`}
          value={value.name}
          onChange={e => set({ name: e.target.value })}
          maxLength={SHOP_LIMITS.name.max}
          disabled={disabled}
          placeholder={t('shop_name_ph')}
        />
        {err('name')}
      </label>

      {/* ── categories (required, multi-select) ──────────────────────────────
          Required because it is what search filters on and what the
          recommendation rail labels a shop with — a shop with none is
          unfindable by anything but its name. */}
      <div className="dpf-field dpf-field--wide">
        <span className="dpf-label">
          {t('shop_categories')} <b className="dpf-req">*</b>
          <span className="dpf-count">{categories.length}/{SHOP_LIMITS.categories.max}</span>
        </span>
        <div className="dpf-chips">
          {SHOP_CATEGORIES.map(key => {
            const on = categories.includes(key);
            const full = !on && categories.length >= SHOP_LIMITS.categories.max;
            return (
              <button
                key={key} type="button"
                className={`dpf-chip${on ? ' dpf-chip--on' : ''}`}
                onClick={() => toggleCategory(key)}
                disabled={disabled || full}
                aria-pressed={on}
              >{t(`shop_cat_${key}`)}</button>
            );
          })}
        </div>
        {err('categories')}
      </div>

      {/* ── delivery ─────────────────────────────────────────────────────────
          A checkbox, not a pair of radios: "no" is the absence of a claim, and
          a shop that has not thought about it should not be forced to answer.
          Search only ever uses this to narrow (see filterShops). */}
      <div className="dpf-field dpf-field--wide">
        <label className={`spf-switch${disabled ? ' spf-switch--off' : ''}`}>
          <input
            type="checkbox"
            checked={!!value.delivery}
            onChange={e => set({ delivery: e.target.checked })}
            disabled={disabled}
          />
          <span className="spf-switch-track" aria-hidden="true"><span className="spf-switch-knob" /></span>
          <span className="spf-switch-body">
            <span className="spf-switch-title">{t('shop_delivery')}</span>
            <span className="dpf-hint">{t('shop_delivery_hint')}</span>
          </span>
        </label>
      </div>

      {/* ── location ── */}
      <div className="dpf-field dpf-field--wide">
        <span className="dpf-label">
          {t('shop_location')} <span className="dpf-opt">{t('ds_optional')}</span>
        </span>
        <LocationPicker
          value={value.location}
          address={value.locationAddress}
          coords={value.locationCoords}
          onChange={patch => set(patch)}
          disabled={disabled}
        />
      </div>

      {/* ── description ── */}
      <label className="dpf-field dpf-field--wide">
        <span className="dpf-label">
          {t('shop_description')} <span className="dpf-opt">{t('ds_optional')}</span>
          <span className="dpf-count">{(value.description || '').length}/{SHOP_LIMITS.description.max}</span>
        </span>
        <textarea
          className="dpf-input dpf-textarea"
          rows={4}
          value={value.description}
          onChange={e => set({ description: e.target.value.slice(0, SHOP_LIMITS.description.max) })}
          disabled={disabled}
          placeholder={t('shop_description_ph')}
        />
      </label>

      {/* ── website ──────────────────────────────────────────────────────────
          One, not the designer's list of three. A shop has a storefront; the
          extra rows would be empty on every profile. */}
      <label className="dpf-field dpf-field--wide">
        <span className="dpf-label">
          {t('shop_website')} <span className="dpf-opt">{t('ds_optional')}</span>
        </span>
        <input
          className="dpf-input"
          value={value.website}
          onChange={e => set({ website: e.target.value })}
          maxLength={SHOP_LIMITS.website.max}
          disabled={disabled}
          placeholder="https://shop.com"
          inputMode="url"
        />
      </label>

      {/* ── socials ──────────────────────────────────────────────────────────
          A fixed set of labelled rows rather than the designer's free-form
          contact list. Every shop has the same handful of channels, and naming
          them is what lets the public profile render the right icon and build
          the right link instead of guessing from a URL. */}
      <div className="dpf-field dpf-field--wide">
        <span className="dpf-label">
          {t('shop_socials')} <span className="dpf-opt">{t('ds_optional')}</span>
        </span>
        <div className="spf-socials">
          {SHOP_SOCIALS.map(({ id, free }) => (
            <div className="spf-social" key={id}>
              <span className="spf-social-label">{t(`shop_social_${id}`)}</span>
              <input
                className="dpf-input"
                value={value.socials?.[id] || ''}
                onChange={e => setSocial(id, e.target.value)}
                maxLength={SHOP_LIMITS.socialValue.max}
                disabled={disabled}
                // Handles, not URLs — see shopSocialHref. A pasted link still
                // works, so the placeholder shows the shorter thing to type.
                placeholder={free ? '+374 00 000000' : `@${t('shop_social_handle')}`}
                inputMode={free ? 'tel' : 'text'}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
